import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Alert
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { API_ENDPOINTS, API_BASE_URL } from "../config/api.config";
import { Tooltip as MuiTooltip } from '@mui/material';
import { Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper } from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { getAbsencesForUsers } from '../utils/absenceService';
import { fetchDirectReportsByPmId } from '../utils/absenceCalendarExport';
import {
  buildAbsencesByUserIdMap,
  fetchMonthlyWorklogSummaryForUsers,
  sortNamesAlphabetically,
  sortUsersByDisplayName,
  accumulateMonthlySummaryCell,
  pivotMinutesForDay,
} from '../utils/peopleManagerApi';
import { isFutureDay, parseLocalDate } from '../utils/dateHelpers';
import { FORECAST_CONFIG } from '../config/local.config';

export default function MonthlyPeopleManagerDetail() {
  const { userId: selectedUserId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Recupera i filtri dalla location state o usa valori di default
  const filterYear = location.state?.filterYear || new Date().getFullYear();
  const filterMonth = location.state?.filterMonth || new Date().getMonth() + 1;
  const filterActivityIds = location.state?.filterActivityIds || [];

  // Nomi dei mesi
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Se l'utente è stato passato tramite location.state, usalo direttamente, altrimenti fai il fetch
  const [selectedUser, setSelectedUser] = useState(location.state?.selectedUser || null);
  const [managedUsers, setManagedUsers] = useState([]); // Utenti gestiti dall'utente selezionato
  const [managedUsersHours, setManagedUsersHours] = useState({}); // Ore degli utenti gestiti
  const [loading, setLoading] = useState(true);

  // Stato per utente selezionato per dettagli inline (utenti non-People Manager)
  const [selectedManagedUserForDetails, setSelectedManagedUserForDetails] = useState(null);
  const [selectedManagedUserProjectData, setSelectedManagedUserProjectData] = useState({});
  const [selectedManagedUserLoading, setSelectedManagedUserLoading] = useState(false);

  const isFetchingData = useRef(false);
  const lastFetchKey = useRef(null);
  const [absencesByUserId, setAbsencesByUserId] = useState({});

  // Calcola giorni del mese selezionato
  const daysInMonth = useMemo(() => {
    return new Date(filterYear, filterMonth, 0).getDate();
  }, [filterYear, filterMonth]);

  // Dimensioni fisse per tabella responsive
  const tableDimensions = useMemo(() => {
    const firstColWidth = 140;
    const dayColWidth = 45;
    const totalColWidth = 60;
    const minWidth = firstColWidth + daysInMonth * dayColWidth + totalColWidth;
    return { firstColWidth, dayColWidth, totalColWidth, minWidth };
  }, [daysInMonth]);

  // Calcola i nomi dei giorni della settimana
  const weekDays = useMemo(() => {
    return Array.from({ length: daysInMonth }, (_, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      return date.toLocaleDateString('en-GB', { weekday: 'short' }).charAt(0);
    });
  }, [filterYear, filterMonth, daysInMonth]);

  // Calcola indici dei weekend
  const weekendIndexes = useMemo(() => {
    return weekDays.map((wd, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      const day = date.getDay();
      return (day === 0 || day === 6);
    });
  }, [weekDays, filterYear, filterMonth]);

  useEffect(() => {
    if (!managedUsers.length) {
      setAbsencesByUserId({});
      return;
    }
    const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    const userIds = managedUsers.map((u) => u.id);
    getAbsencesForUsers(userIds, yearMonth)
      .then((entries) => setAbsencesByUserId(buildAbsencesByUserIdMap(entries)))
      .catch(() => setAbsencesByUserId({}));
  }, [filterYear, filterMonth, managedUsers]);

  const userIdByUsername = useMemo(() => {
    const map = {};
    (managedUsers || []).forEach((u) => {
      const name = u.name || u.username;
      if (name && u.id != null) map[name] = String(u.id);
    });
    return map;
  }, [managedUsers]);

  const isUserAbsent = (username, dayNumber) => {
    const uid = userIdByUsername[username];
    if (!uid) return null;
    return absencesByUserId[uid]?.get(dayNumber) || null;
  };

  // Funzione per formattare i nomi utente
  const formatUserName = (fullName) => {
    if (!fullName || typeof fullName !== 'string') return fullName;
    const parts = fullName.trim().split(' ');
    if (parts.length <= 1) return fullName;
    const firstPart = parts[0];
    const abbreviatedFirst = firstPart.charAt(0) + '.';
    const restOfName = parts.slice(1).join(' ');
    return `${abbreviatedFirst} ${restOfName}`;
  };

  // Aggiorna selectedUser quando cambia location.state.selectedUser (navigazione tra People Manager)
  useEffect(() => {
    if (location.state?.selectedUser) {
      console.log('Updating selectedUser from location.state:', location.state.selectedUser);
      setSelectedUser(location.state.selectedUser);
    }
  }, [location.state?.selectedUser?.id]); // Dipende solo dall'ID per evitare loop

  // Carica i dati dell'utente selezionato e dei suoi utenti gestiti
  useEffect(() => {
    if (!selectedUserId) {
      setLoading(false);
      return;
    }

    const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    const activityIdsKey = (filterActivityIds && filterActivityIds.length > 0) ? filterActivityIds.sort().join(',') : 'all';
    const fetchKey = `${selectedUserId}-${yearMonth}-${activityIdsKey}`;

    if (isFetchingData.current) {
      return;
    }

    if (lastFetchKey.current === fetchKey) {
      return;
    }

    if (lastFetchKey.current && lastFetchKey.current !== fetchKey) {
      isFetchingData.current = false;
    }

    isFetchingData.current = true;
    lastFetchKey.current = fetchKey;
    setLoading(true);

    // Fetch info utente selezionato SOLO se non è già stato impostato da location.state
    if (!selectedUser || selectedUser.id !== Number(selectedUserId)) {
      console.log('Fetching user data for ID:', selectedUserId);
      fetch(`${API_ENDPOINTS.USERS}?id=${selectedUserId}`)
        .then(res => res.json())
        .then(data => {
          console.log('Fetched user data:', data);
          // L'API con ?id= restituisce direttamente l'oggetto utente, non {users: [...]}
          const user = data.users ? (data.users.find(u => u.id === Number(selectedUserId)) || data.users[0]) : data;
          console.log('Found user:', user);
          if (user && user.id) {
            const userName = user.username || (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email);
            console.log('Setting selectedUser with name:', userName);
            setSelectedUser({
              id: user.id,
              name: userName,
              username: userName,
              email: user.email
            });
          } else {
            console.error('User not found in response for ID:', selectedUserId);
          }
        })
        .catch(err => console.error('Error loading user:', err));
    } else {
      console.log('Skipping user fetch, already have user data:', selectedUser);
    }

    // Fetch utenti gestiti dall'utente selezionato
    fetchDirectReportsByPmId(selectedUserId)
      .then(async (formattedUsers) => {
        const sortedUsers = sortUsersByDisplayName(formattedUsers);
        setManagedUsers(sortedUsers);

        if (sortedUsers.length > 0) {
          try {
            const data = await fetchMonthlyWorklogSummaryForUsers(
              sortedUsers.map((u) => u.id),
              yearMonth,
              filterActivityIds.length > 0 ? filterActivityIds : []
            );
            const byUser = {};
            data.forEach((item) => {
              const username = item.username || sortedUsers.find((u) => u.id === item.user_id)?.name || 'Unknown';
              if (!byUser[username]) byUser[username] = {};
              const day = parseLocalDate(item.work_date).getDate();
              byUser[username][day] = accumulateMonthlySummaryCell(byUser[username][day], item);
            });
            sortedUsers.forEach((user) => {
              const name = user.name || user.username;
              if (!byUser[name]) byUser[name] = {};
            });
            setManagedUsersHours(byUser);
          } catch (err) {
            console.error('Error loading worklogs:', err);
            setManagedUsersHours({});
          }
        }
        isFetchingData.current = false;
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading managed users:', err);
        setManagedUsers([]);
        isFetchingData.current = false;
        setLoading(false);
      });
  }, [selectedUserId, filterMonth, filterYear, filterActivityIds]);

  // Fetch dati progetti per utente gestito selezionato (quando si clicca sul nome)
  useEffect(() => {
    if (!selectedManagedUserForDetails) {
      setSelectedManagedUserLoading(false);
      setSelectedManagedUserProjectData({});
      return;
    }

    const userObj = managedUsers.find(u => (u.name || u.username) === selectedManagedUserForDetails);
    if (!userObj || !userObj.id) {
      setSelectedManagedUserLoading(false);
      setSelectedManagedUserProjectData({});
      return;
    }

    setSelectedManagedUserLoading(true);

    const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    const activityIdsParam = (filterActivityIds && filterActivityIds.length > 0)
      ? `&activity_ids=${encodeURIComponent(filterActivityIds.join(','))}`
      : '';

    fetch(`${API_BASE_URL}/monthly_worklog_summary_user_project?user_id=${encodeURIComponent(userObj.id)}&year_month=${yearMonth}${activityIdsParam}&include_forecast=true`)
      .then(res => res.json())
      .then(data => {
        const byProject = {};
        if (Array.isArray(data.data)) {
          data.data.forEach(item => {
            const projectName = item.project_name || 'Unknown Project';
            if (!byProject[projectName]) byProject[projectName] = {};
            const day = parseLocalDate(item.work_date).getDate();
            byProject[projectName][day] = accumulateMonthlySummaryCell(byProject[projectName][day], item);
          });
        }
        setSelectedManagedUserProjectData(byProject);
        setSelectedManagedUserLoading(false);
      })
      .catch(err => {
        console.error('Error loading managed user project details:', err);
        setSelectedManagedUserLoading(false);
        setSelectedManagedUserProjectData({});
      });
  }, [selectedManagedUserForDetails, filterMonth, filterYear, managedUsers, filterActivityIds]);

  // Render tabella pivot per progetti (ore dell'utente selezionato)
  function renderProjectPivotTable({ projectPivotData, filterYear, filterMonth, selectedUsername, isUserAbsentFn }) {
    const allProjects = projectPivotData ? Object.keys(projectPivotData) : [];
    if (allProjects.length === 0) return null;

    const projectTotals = allProjects.map(p => {
      if (projectPivotData && projectPivotData[p]) {
        return Math.round((Object.entries(projectPivotData[p]).reduce((sum, [day, obj]) => (
          sum + pivotMinutesForDay(obj, filterYear, filterMonth, Number(day))
        ), 0) / 60) * 100) / 100;
      }
      return 0;
    });

    const dayTotals = Array.from({ length: daysInMonth }, (_, i) => {
      let total = 0;
      allProjects.forEach(p => {
        total += pivotMinutesForDay(projectPivotData?.[p]?.[i + 1], filterYear, filterMonth, i + 1);
      });
      return Math.round((total / 60) * 100) / 100;
    });
    const grandTotal = Math.round(projectTotals.reduce((a, b) => a + b, 0) * 100) / 100;

    return (
      <TableContainer component={Paper} sx={{
        borderRadius: 2,
        bgcolor: 'white',
        p: 0,
        maxWidth: '100vw',
        overflowX: 'auto',
        boxShadow: 'none',
        border: '1px solid #f3f3f3',
        mt: 4
      }}>
        <Table size="small" sx={{
          minWidth: tableDimensions.minWidth,
          tableLayout: 'fixed',
          fontSize: '0.85rem',
          width: '100%',
          bgcolor: 'white'
        }}>
          <colgroup>
            <col style={{ width: tableDimensions.firstColWidth }} />
            {Array.from({ length: daysInMonth }).map((_, i) => (
              <col key={i} style={{ width: tableDimensions.dayColWidth }} />
            ))}
            <col style={{ width: tableDimensions.totalColWidth }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2} sx={{
                bgcolor: '#5ab031',
                color: 'white',
                fontWeight: 'normal',
                fontSize: '0.85rem',
                border: 'none',
                borderRight: '0px solid #e0e0e0',
                textAlign: 'center',
                minWidth: 120,
                maxWidth: 160,
                width: 140,
                position: 'sticky',
                left: 0,
                zIndex: 3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>
                Project
              </TableCell>
              {weekDays.map((wd, i) => (
                <TableCell key={i} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{wd}</TableCell>
              ))}
              <TableCell rowSpan={2} sx={{
                bgcolor: '#5ab031',
                color: 'white',
                fontWeight: 'normal',
                fontSize: '0.85rem',
                p: 0.5,
                border: '0px solid #f3f3f3',
                position: 'sticky',
                right: 0,
                zIndex: 3,
                textAlign: 'center',
                minWidth: 60,
                maxWidth: 80,
                width: 60,
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  right: 0,
                  zIndex: 3,
                  boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>Tot</TableCell>
            </TableRow>
            <TableRow>
              {Array.from({ length: daysInMonth }, (_, i) => (
                <TableCell key={i + 1} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{i + 1}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {allProjects.map((p, idx) => (
              <TableRow key={p} hover sx={{ '&:hover': { bgcolor: '#e6f4ea' } }}>
                <TableCell sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  minWidth: 120,
                  maxWidth: 160,
                  width: 140,
                  p: 0.5,
                  border: '1px solid #f3f3f3',
                  borderRight: '2px solid #5ab031',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textAlign: 'left',
                  bgcolor: 'white'
                }}>
                  {p}
                </TableCell>
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const obj = projectPivotData?.[p]?.[i + 1];
                  const dayNum = i + 1;
                  const dateStr = `${filterYear}-${String(filterMonth).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                  const future = isFutureDay(dateStr);
                  const minutes = future ? (Number(obj?.forecast_effort) || 0) : (Number(obj?.actual_effort ?? obj?.total_effort) || 0);
                  const displayValue = minutes ? Math.round((minutes / 60) * 100) / 100 : '';
                  const absEntry = selectedUsername && isUserAbsentFn ? isUserAbsentFn(selectedUsername, dayNum) : null;
                  const isWeekend = weekendIndexes[i];
                  return (
                    <MuiTooltip key={i + 1} title={absEntry ? `Absence (${absEntry.full_day ? 'full day' : 'partial'})${displayValue ? ` · ${displayValue}h` : ''}${future ? ' (forecast)' : ''}` : (future ? 'Forecast' : '')} arrow>
                    <TableCell sx={{
                      textAlign: 'center',
                      bgcolor: absEntry ? (absEntry.full_day ? '#ffcdd2 !important' : '#fff9c4 !important') : isWeekend ? 'rgba(55,55,55,0.08)' : (future && obj?.forecast_effort ? FORECAST_CONFIG.forecastCellBg : 'white'),
                      color: future && displayValue ? FORECAST_CONFIG.forecastCellColor : 'inherit',
                      fontSize: '0.7rem',
                      p: 0.5,
                      border: '1px solid #f3f3f3',
                      fontWeight: future && displayValue ? 600 : 'normal',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontVariantNumeric: 'tabular-nums',
                      '&:hover': { bgcolor: absEntry ? (absEntry.full_day ? '#ef9a9a !important' : '#fff176 !important') : '#aace7c78' }
                    }}>
                      {displayValue ? displayValue : absEntry ? <EventBusyIcon sx={{ fontSize: '0.7rem', color: absEntry.full_day ? '#c62828' : '#f57f17', verticalAlign: 'middle' }} /> : ''}
                    </TableCell>
                    </MuiTooltip>
                  );
                })}
                <TableCell sx={{
                  textAlign: 'center',
                  fontSize: '0.7rem',
                  p: 0.5,
                  border: '1px solid #f3f3f3',
                  position: 'sticky',
                  right: 0,
                  zIndex: 2,
                  borderLeft: '2px solid #5ab031',
                  minWidth: 60,
                  maxWidth: 80,
                  width: 60,
                  bgcolor: 'white !important',
                  color: 'black !important',
                  fontWeight: 'bold',
                  '@media (max-width: 900px)': {
                    position: 'sticky',
                    right: 0,
                    zIndex: 2,
                    boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                  }
                }}>{projectTotals[idx]}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: '#248b7e' }}>
              <TableCell sx={{
                bgcolor: 'white',
                position: 'sticky',
                left: 0,
                zIndex: 2,
                minWidth: 120,
                maxWidth: 160,
                width: 140,
                p: 0.5,
                border: '1px solid #f3f3f3',
                borderRight: '2px solid #5ab031',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textAlign: 'right',
                borderTop: '2px solid #5ab031',
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>Tot</TableCell>
              {dayTotals.map((tot, i) => (
                <TableCell key={i + 1} sx={{ bgcolor: 'white !important', color: 'black !important', textAlign: 'center', fontSize: '0.7rem', p: 0.5, border: '1px solid #f3f3f3', borderTop: '2px solid #5ab031', fontWeight: 'bold' }}>{tot}</TableCell>
              ))}
              <TableCell sx={{
                bgcolor: 'white !important',
                color: 'black !important',
                textAlign: 'center',
                fontSize: '0.7rem',
                p: 0.5,
                border: '1px solid #f3f3f3',
                position: 'sticky',
                right: 0,
                zIndex: 2,
                borderLeft: '2px solid #5ab031',
                borderTop: '2px solid #5ab031',
                minWidth: 60,
                maxWidth: 80,
                width: 60,
                fontWeight: 'bold',
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  right: 0,
                  zIndex: 2,
                  boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>{grandTotal}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Render tabella pivot per utenti gestiti
  function renderUsersPivotTable({ usersPivotData, filterYear, filterMonth }) {
    const allUsers = sortNamesAlphabetically(usersPivotData ? Object.keys(usersPivotData) : []);
    if (allUsers.length === 0) return null;

    const userTotals = allUsers.map(u => {
      if (usersPivotData && usersPivotData[u]) {
        return Math.round((Object.entries(usersPivotData[u]).reduce((sum, [day, obj]) => (
          sum + pivotMinutesForDay(obj, filterYear, filterMonth, Number(day))
        ), 0) / 60) * 100) / 100;
      }
      return 0;
    });

    const dayTotals = Array.from({ length: daysInMonth }, (_, i) => {
      let total = 0;
      allUsers.forEach(u => {
        total += pivotMinutesForDay(usersPivotData?.[u]?.[i + 1], filterYear, filterMonth, i + 1);
      });
      return Math.round((total / 60) * 100) / 100;
    });
    const grandTotal = Math.round(userTotals.reduce((a, b) => a + b, 0) * 100) / 100;

    return (
      <TableContainer component={Paper} sx={{
        borderRadius: 2,
        bgcolor: 'white',
        p: 0,
        maxWidth: '100vw',
        overflowX: 'auto',
        boxShadow: 'none',
        border: '1px solid #f3f3f3',
        mt: 4
      }}>
        <Table size="small" sx={{
          minWidth: tableDimensions.minWidth,
          tableLayout: 'fixed',
          fontSize: '0.85rem',
          width: '100%',
          bgcolor: 'white'
        }}>
          <colgroup>
            <col style={{ width: tableDimensions.firstColWidth }} />
            {Array.from({ length: daysInMonth }).map((_, i) => (
              <col key={i} style={{ width: tableDimensions.dayColWidth }} />
            ))}
            <col style={{ width: tableDimensions.totalColWidth }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2} sx={{
                bgcolor: '#5ab031',
                color: 'white',
                fontWeight: 'normal',
                fontSize: '0.85rem',
                border: 'none',
                borderRight: '0px solid #e0e0e0',
                textAlign: 'center',
                minWidth: 120,
                maxWidth: 160,
                width: 140,
                position: 'sticky',
                left: 0,
                zIndex: 3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>
                User
              </TableCell>
              {weekDays.map((wd, i) => (
                <TableCell key={i} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{wd}</TableCell>
              ))}
              <TableCell rowSpan={2} sx={{
                bgcolor: '#5ab031',
                color: 'white',
                fontWeight: 'normal',
                fontSize: '0.85rem',
                p: 0.5,
                border: '0px solid #f3f3f3',
                position: 'sticky',
                right: 0,
                zIndex: 3,
                textAlign: 'center',
                minWidth: 60,
                maxWidth: 80,
                width: 60,
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  right: 0,
                  zIndex: 3,
                  boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>Tot</TableCell>
            </TableRow>
            <TableRow>
              {Array.from({ length: daysInMonth }, (_, i) => (
                <TableCell key={i + 1} sx={{ bgcolor: '#5ab031', color: 'white', border: '0px solid #f3f3f3', textAlign: 'center', fontWeight: 'normal', fontSize: '0.85rem', p: 0.5 }}>{i + 1}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {allUsers.map((u, idx) => {
              const userObj = managedUsers.find(mu => (mu.name || mu.username) === u);
              const isPeopleManager = userObj?.isPeopleManager || false;

              return (
                <TableRow
                  key={u}
                  hover
                  sx={{
                    '&:hover': { bgcolor: '#e6f4ea' }
                  }}
                >
                  <TableCell sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    minWidth: 120,
                    maxWidth: 160,
                    width: 140,
                    p: 0.5,
                    border: '1px solid #f3f3f3',
                    borderRight: '2px solid #5ab031',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textAlign: 'left',
                    bgcolor: 'white',
                    '@media (max-width: 900px)': {
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                    }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <MuiTooltip title="View project details" arrow>
                        <span
                          style={{
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontWeight: 'normal',
                            color: '#5ab031',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.color = '#4a9b2a';
                            e.target.style.fontWeight = 'bold';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = '#5ab031';
                            e.target.style.fontWeight = 'normal';
                          }}
                          onClick={() => {
                            if (selectedManagedUserForDetails === u) {
                              setSelectedManagedUserForDetails(null);
                            } else {
                              setSelectedManagedUserForDetails(u);
                            }
                          }}
                        >
                          {formatUserName(u)}
                        </span>
                      </MuiTooltip>
                      {isPeopleManager && userObj && (
                        <MuiTooltip title="View hierarchy" arrow>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              bgcolor: 'rgba(90, 176, 49, 0.1)',
                              borderRadius: '4px',
                              padding: '2px 4px',
                              marginLeft: '4px',
                              flexShrink: 0,
                              '&:hover': {
                                bgcolor: 'rgba(90, 176, 49, 0.2)',
                                transform: 'scale(1.05)'
                              },
                              transition: 'all 0.2s'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/monthly-people-mng-view/user/${userObj.id}`, {
                                state: {
                                  filterYear,
                                  filterMonth,
                                  filterActivityIds,
                                  from: `/monthly-people-mng-view/user/${selectedUserId}`,
                                  selectedUser: {
                                    id: userObj.id,
                                    name: userObj.name || userObj.username,
                                    username: userObj.username,
                                    email: userObj.email
                                  }
                                }
                              });
                            }}
                          >
                            <SupervisorAccountIcon 
                              sx={{ 
                                fontSize: '1.1rem', 
                                color: '#5ab031'
                              }}
                            />
                          </Box>
                        </MuiTooltip>
                      )}
                    </Box>
                  </TableCell>
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const obj = usersPivotData[u]?.[i + 1];
                    const dayNum = i + 1;
                    const dateStr = `${filterYear}-${String(filterMonth).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                    const future = isFutureDay(dateStr);
                    const minutes = pivotMinutesForDay(obj, filterYear, filterMonth, dayNum);
                    const value = minutes ? Math.round((minutes / 60) * 100) / 100 : '';
                    const userObj = managedUsers.find(mu => mu.name === u || mu.username === u || `${mu.first_name} ${mu.last_name}` === u);
                    const pivotUid = (() => {
                      const anyDay = Object.values(usersPivotData[u] || {})[0];
                      return anyDay?.user_id ? String(anyDay.user_id) : null;
                    })();
                    const uid = userObj ? String(userObj.id) : pivotUid;
                    const absEntry = uid ? (absencesByUserId[uid]?.get(dayNum) || null) : null;
                    return (
                      <MuiTooltip key={i + 1} title={absEntry ? `Absence (${absEntry.full_day ? 'full day' : 'partial'})${value ? ` · ${value}h` : ''}${future ? ' (forecast)' : ''}` : (future ? 'Forecast' : '')} arrow>
                      <TableCell sx={{
                        textAlign: 'center',
                        bgcolor: absEntry ? (absEntry.full_day ? '#ffcdd2 !important' : '#fff9c4 !important') : weekendIndexes[i] ? 'rgba(55,55,55,0.08)' : (future && obj?.forecast_effort ? FORECAST_CONFIG.forecastCellBg : 'white'),
                        color: future && value ? FORECAST_CONFIG.forecastCellColor : 'inherit',
                        fontSize: '0.7rem',
                        p: 0.5,
                        border: '1px solid #f3f3f3',
                        fontWeight: future && value ? 600 : 'normal',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontVariantNumeric: 'tabular-nums',
                        '&:hover': { bgcolor: absEntry ? (absEntry.full_day ? '#ef9a9a !important' : '#fff176 !important') : '#aace7c78' }
                      }}>
                        {obj ? (
                          <MuiTooltip
                            title={
                              <span>
                                {future && obj.forecast_effort ? <div><b>Forecast:</b> {Math.round((Number(obj.forecast_effort) / 60) * 100) / 100} h</div> : null}
                                {!future && obj.total_effort ? <div><b>Effort:</b> {Math.round((Number(obj.actual_effort ?? obj.total_effort) / 60) * 100) / 100} h</div> : null}
                                {obj.ticket_id && <div><b>Ticket:</b> {obj.ticket_id}</div>}
                                {obj.description && <div><b>Desc:</b> {obj.description}</div>}
                                {obj.activity_name && <div><b>Activity:</b> {obj.activity_name}</div>}
                              </span>
                            }
                            arrow
                          >
                            <span>{value}</span>
                          </MuiTooltip>
                        ) : absEntry ? <EventBusyIcon sx={{ fontSize: '0.7rem', color: absEntry.full_day ? '#c62828' : '#f57f17', verticalAlign: 'middle' }} /> : value}
                      </TableCell>
                      </MuiTooltip>
                    );
                  })}
                  <TableCell sx={{
                    textAlign: 'center',
                    fontSize: '0.7rem',
                    p: 0.5,
                    border: '1px solid #f3f3f3',
                    position: 'sticky',
                    right: 0,
                    zIndex: 2,
                    borderLeft: '2px solid #5ab031',
                    minWidth: 60,
                    maxWidth: 80,
                    width: 60,
                    bgcolor: 'white !important',
                    color: 'black !important',
                    fontWeight: 'bold',
                    '@media (max-width: 900px)': {
                      position: 'sticky',
                      right: 0,
                      zIndex: 2,
                      boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                    }
                  }}>{userTotals[idx]}</TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ bgcolor: '#248b7e' }}>
              <TableCell sx={{
                bgcolor: 'white',
                position: 'sticky',
                left: 0,
                zIndex: 2,
                minWidth: 120,
                maxWidth: 160,
                width: 140,
                p: 0.5,
                border: '1px solid #f3f3f3',
                borderRight: '2px solid #5ab031',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textAlign: 'right',
                borderTop: '2px solid #5ab031',
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>Tot</TableCell>
              {dayTotals.map((tot, i) => (
                <TableCell key={i + 1} sx={{ bgcolor: 'white !important', color: 'black !important', textAlign: 'center', fontSize: '0.7rem', p: 0.5, border: '1px solid #f3f3f3', borderTop: '2px solid #5ab031', fontWeight: 'bold' }}>{tot}</TableCell>
              ))}
              <TableCell sx={{
                bgcolor: 'white !important',
                color: 'black !important',
                textAlign: 'center',
                fontSize: '0.7rem',
                p: 0.5,
                border: '1px solid #f3f3f3',
                position: 'sticky',
                right: 0,
                zIndex: 2,
                borderLeft: '2px solid #5ab031',
                borderTop: '2px solid #5ab031',
                minWidth: 60,
                maxWidth: 80,
                width: 60,
                fontWeight: 'bold',
                '@media (max-width: 900px)': {
                  position: 'sticky',
                  right: 0,
                  zIndex: 2,
                  boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                }
              }}>{grandTotal}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header con pulsante indietro */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton
          onClick={() => {
            // Se c'è uno stato di navigazione precedente, torna lì, altrimenti alla vista principale
            if (location.state?.from) {
              navigate(location.state.from, {
                state: {
                  filterYear,
                  filterMonth,
                  filterActivityIds
                }
              });
            } else {
              navigate('/monthly-people-mng-view', {
                state: {
                  filterYear,
                  filterMonth,
                  filterActivityIds
                }
              });
            }
          }}
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
          People Manager Details
        </Typography>
      </Box>

      {/* Info utente - Card compatta */}
      <Box sx={{ 
        mb: 2, 
        p: 2, 
        bgcolor: '#f8fdf5', 
        borderRadius: 1.5,
        border: '2px solid #5ab031',
        boxShadow: '0 1px 4px rgba(90, 176, 49, 0.12)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <SupervisorAccountIcon sx={{ fontSize: '1.5rem', color: '#5ab031', mr: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#5ab031', fontSize: '1.25rem' }}>
            {selectedUser?.name || 'Loading...'}
          </Typography>
        </Box>
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(200px, 1fr))' },
          gap: 1.5,
          mt: 1
        }}>
          {selectedUser?.email && (
            <Box>
              <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 'bold' }}>
                Email
              </Typography>
              <Typography variant="body2" sx={{ color: '#333', mt: 0.2, fontSize: '0.875rem' }}>
                {selectedUser.email}
              </Typography>
            </Box>
          )}
          
          <Box>
            <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 'bold' }}>
              Period
            </Typography>
            <Typography variant="body2" sx={{ color: '#333', mt: 0.2, fontSize: '0.875rem' }}>
              {monthNames[filterMonth - 1]} {filterYear}
            </Typography>
          </Box>
          
          {!loading && managedUsers.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 'bold' }}>
                Managed Users
              </Typography>
              <Typography variant="body2" sx={{ color: '#333', mt: 0.2, fontSize: '0.875rem' }}>
                {managedUsers.length} {managedUsers.length === 1 ? 'user' : 'users'}
              </Typography>
            </Box>
          )}
          
          {filterActivityIds && filterActivityIds.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 'bold' }}>
                Filters
              </Typography>
              <Typography variant="body2" sx={{ color: '#333', mt: 0.2, fontSize: '0.875rem' }}>
                {filterActivityIds.length} {filterActivityIds.length === 1 ? 'activity' : 'activities'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Tabella utenti gestiti */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <CircularProgress size={40} />
          <Typography sx={{ ml: 2 }}>Loading user details...</Typography>
        </Box>
      ) : (
        <>
          {managedUsers.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>Managed Users</Typography>
              {renderUsersPivotTable({ usersPivotData: managedUsersHours, filterYear, filterMonth })}

              {/* Tabella dettagliata inline per utenti non-People Manager */}
              {selectedManagedUserForDetails && (
                <Box sx={{ mt: 3 }}>
                  {selectedManagedUserLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ ml: 2, color: '#666' }}>
                        Loading project details...
                      </Typography>
                    </Box>
                  ) : selectedManagedUserProjectData && Object.keys(selectedManagedUserProjectData).length > 0 ? (
                    <>
                      <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
                        Project Details: {selectedManagedUserForDetails}
                      </Typography>
                      {renderProjectPivotTable({
                        projectPivotData: selectedManagedUserProjectData,
                        filterYear,
                        filterMonth,
                        selectedUsername: selectedManagedUserForDetails,
                        isUserAbsentFn: isUserAbsent,
                      })}
                    </>
                  ) : (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      No project data available for this user in the selected period.
                    </Alert>
                  )}
                </Box>
              )}
            </Box>
          )}

          {managedUsers.length === 0 && (
            <Alert severity="info">This user does not manage any other users.</Alert>
          )}
        </>
      )}
    </Box>
  );
}